import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCompanyProfile } from '../../../Redux/companySlice';

/**
 * Billing and contact details for checkout.
 * - Name: pre-filled from company, editable (required)
 * - Email: company email (fetched), read-only — subscription link is sent here
 * - Mobile: 10-digit number (required)
 */
const CheckoutBillingForm = ({ plan, cycle, formData, setFormData, errors, setErrors }) => {
  const dispatch = useDispatch();
  const company = useSelector((state) => state.company?.company);

  useEffect(() => {
    if (company?.email) {
      setFormData((prev) => ({
        ...prev,
        email: company.email || prev.email,
        name: prev.name || company.companyName || [company.firstName, company.lastName].filter(Boolean).join(' ') || '',
      }));
    }
  }, [company?.email, company?.companyName, company?.firstName, company?.lastName]);

  useEffect(() => {
    if (plan && cycle) {
      dispatch(fetchCompanyProfile()).catch(() => {});
    }
  }, [dispatch, plan, cycle]);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
  };

  return (
    <div className="space-y-4 mb-6">
      <h3 className="text-sm font-semibold text-gray-700">Billing & contact</h3>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
        <input
          type="text"
          value={formData.name || ''}
          onChange={handleChange('name')}
          placeholder="Full name or company name"
          className={`w-full px-3 py-2 border rounded-lg text-sm ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
        />
        {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Email <span className="text-gray-400">(subscription link will be sent here)</span>
        </label>
        <input
          type="email"
          value={formData.email || ''}
          readOnly
          disabled
          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-600"
        />
        <p className="text-xs text-gray-500 mt-1">Company email cannot be changed here.</p>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Mobile number</label>
        <input
          type="tel"
          value={formData.mobile || ''}
          onChange={handleChange('mobile')}
          placeholder="10-digit mobile number"
          maxLength={10}
          className={`w-full px-3 py-2 border rounded-lg text-sm ${errors.mobile ? 'border-red-500' : 'border-gray-300'}`}
        />
        {errors.mobile && <p className="text-xs text-red-500 mt-1">{errors.mobile}</p>}
      </div>

      <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
        <p className="font-semibold mb-1">Subscription terms</p>
        {cycle === 'monthly' ? (
          <p>
            You are purchasing a <strong>monthly</strong> subscription to {plan?.name || 'this plan'}. The amount will be deducted every month from your account until you cancel the subscription. You may cancel anytime from your account settings.
          </p>
        ) : (
          <p>
            You are purchasing a <strong>yearly</strong> subscription to {plan?.name || 'this plan'}. The amount will be charged annually from your account until you cancel the subscription. You may cancel anytime from your account settings; access continues until the end of the current billing period.
          </p>
        )}
      </div>
    </div>
  );
};

export default CheckoutBillingForm;
